// Reconciliation API client. Thin wrapper over the global Axios instance in
// `frontend/src/api.js` — that instance already adds the X-User-Id header.
//
// Endpoints match SPEC §6 verbatim. If the backend hasn't shipped a given
// route yet, callers get a 404; the UI is expected to surface an empty
// state, not crash.

import api from '../api';

const BASE = '/reconciliation';

export function fetchWorkspace(period) {
  return api.get(`${BASE}/workspace/`, { params: { period } });
}

export function suggest(sapIds, invoiceIds) {
  return api.post(`${BASE}/suggest/`, {
    sap_ids: sapIds,
    invoice_ids: invoiceIds,
  });
}

export function reconcile(sapIds, invoiceIds, { period, policy = 'strict', notes } = {}) {
  const body = { sap_ids: sapIds, invoice_ids: invoiceIds, policy };
  if (period) body.period = period;
  if (notes) body.notes = notes;
  return api.post(`${BASE}/reconcile/`, body);
}

// Reconcile a whole VAT/Lux batch against its SAP payment row(s). The batch is
// matched on its payment total (VAT amount), not its gross member invoices.
export function reconcileBatch(sapIds, batchKind, batchId, { period, policy = 'strict' } = {}) {
  const body = { sap_ids: sapIds, batch_kind: batchKind, batch_id: batchId, policy };
  if (period) body.period = period;
  return api.post(`${BASE}/reconcile-batch/`, body);
}

export function unreconcile(groupId) {
  return api.post(`${BASE}/matches/${groupId}/unreconcile/`);
}

export function setAside(sapIds, reason) {
  return api.post(`${BASE}/set-aside/`, { sap_ids: sapIds, reason });
}

// Reconciled-tab listing. SPEC §6.6.
//   category: comma-separated MatchCategory list. Default server-side is
//             vendor_invoice (id_exact + fuzzy_high + manual). We let the
//             caller override.
export function fetchReconciled(period, { limit = 50, offset = 0, q, category } = {}) {
  return api.get(`${BASE}/matches/`, {
    params: { period, limit, offset, q, category },
  });
}

export function uploadSapPull(file, period) {
  const form = new FormData();
  form.append('file', file);
  if (period) form.append('period', period);
  return api.post(`${BASE}/uploads/`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export function fetchUploads(period, { limit = 20, offset = 0 } = {}) {
  return api.get(`${BASE}/uploads/`, { params: { period, limit, offset } });
}

export function fetchBudgetActual(period, { breakdown = 'category' } = {}) {
  return api.get(`${BASE}/budget-actual/`, { params: { period, breakdown } });
}

export function fetchPeriods() {
  return api.get(`${BASE}/periods/`);
}

// Operator tolerance knobs (issue #40). Values are percent strings ("5" = 5%):
//   { recon_auto_fuzzy_tolerance_pct, recon_id_match_tolerance_pct,
//     recon_manual_review_tolerance_pct }
export function fetchReconSettings() {
  return api.get(`${BASE}/settings/`);
}

// PATCH any subset of the tolerance keys (same shape). 400 on a bad value.
export function patchReconSettings(body) {
  return api.patch(`${BASE}/settings/`, body);
}
